"use client";
import React from 'react';
import { sanitizeCmsHtml } from '@/lib/sanitize-cms-html';
import { motion, AnimatePresence } from 'framer-motion';

interface AccordionProps {
  item: {
    id: string;
    title: string;
    content: string;
  };
  isOpen: boolean;
  onToggle: () => void;
}

export default function Accordion({item, isOpen, onToggle}: AccordionProps) {
  return (
      <motion.div
          className="bg-white rounded-lg shadow-faqs overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
              opacity: 1, 
              y: 0,
              scale: isOpen ? 1.01 : 1,
              boxShadow: isOpen ? "0 10px 40px rgba(0, 0, 0, 0.1)" : "0 2px 8px rgba(0, 0, 0, 0.05)"
          }}
          transition={{ 
              type: "spring" as const,
              stiffness: 300,
              damping: 30
          }}
      >
          <motion.button
              type="button"
              id={`accordion-button-${item.id}`}
              onClick={onToggle}
              className="w-full h-[70px] px-6 py-5 text-left flex justify-between items-center cursor-pointer"
              aria-expanded={isOpen}
              aria-controls={`accordion-content-${item.id}`}
              whileHover={{ 
                  backgroundColor: "rgba(227, 30, 36, 0.02)",
                  x: 3
              }}
              whileTap={{ scale: 0.99 }}
              transition={{ type: "spring" as const, stiffness: 400, damping: 20 }}
          >
              <h3 className="font-medium text-alsero-dark-gray text-lg leading-relaxed">
                  {item.title}
              </h3>
              <motion.div
                  animate={{ 
                      rotate: isOpen ? 180 : 0,
                      scale: isOpen ? 1.1 : 1
                  }}
                  transition={{ 
                      type: "spring" as const,
                      stiffness: 200,
                      damping: 15
                  }}
                  className="flex-shrink-0 ml-4"
              >
                  <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      className={isOpen ? "text-alsero-first" : "text-alsero-dark-gray"}
                      aria-hidden="true"
                      focusable="false"
                  >
                      <path
                          d="M8 2V14M2 8H14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                      />
                  </svg>
              </motion.div>
          </motion.button>

          <AnimatePresence initial={false}>
              {isOpen && (
                  <motion.div
                      id={`accordion-content-${item.id}`}
                      role="region"
                      aria-labelledby={`accordion-button-${item.id}`}
                      initial={{ 
                          height: 0, 
                          opacity: 0,
                          y: -10
                      }}
                      animate={{ 
                          height: "auto", 
                          opacity: 1,
                          y: 0
                      }}
                      exit={{ 
                          height: 0, 
                          opacity: 0,
                          y: -10
                      }}
                      transition={{ 
                          height: {
                              type: "spring" as const,
                              stiffness: 300,
                              damping: 30,
                              mass: 0.8
                          },
                          opacity: {
                              duration: 0.4,
                              ease: "easeInOut"
                          },
                          y: {
                              type: "spring" as const,
                              stiffness: 300,
                              damping: 25
                          }
                      }}
                      className="overflow-hidden"
                  >
                      <motion.div 
                          className="px-6 pb-5 pt-2"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ 
                              duration: 0.3,
                              delay: isOpen ? 0.1 : 0
                          }}
                      >
                          <div
                              className="text-alsero-light-gray leading-relaxed text-sm"
                              dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(item.content) }}
                          />
                      </motion.div>
                  </motion.div>
              )}
          </AnimatePresence>
      </motion.div>
  );
};
